import type { CoachingQuestion } from '../../types'
import {
  hintTextStyle,
  mergeHeaderStyle,
  seriesNumberStyle,
  seriesProblemStyle,
} from '../styles'
import type { FollowUpData, FollowUpInput, FollowUpPhase } from '../types'
import { SourceIcon } from './source-icon'

interface FollowUpHeaderProps {
  targetProblem: CoachingQuestion | null
  existingFollowUps: FollowUpData[]
  followUpPhase: FollowUpPhase
  followUpInput: FollowUpInput
}

const completedItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
  padding: '6px 10px',
  backgroundColor: '#f0fdf4',
  borderRadius: '6px',
  border: '1px solid #bbf7d0',
}

const completedLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#15803d',
  fontWeight: 600,
  minWidth: '50px',
}

const completedValueStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#166534',
  fontWeight: 500,
}

export function FollowUpHeader({
  targetProblem,
  existingFollowUps,
  followUpPhase,
  followUpInput,
}: FollowUpHeaderProps) {
  const getStepNumber = () => {
    switch (followUpPhase) {
      case 'select-problem':
        return 1
      case 'enter-prompt':
        return 2
      case 'enter-time-complexity':
        return 3
      case 'enter-space-complexity':
        return 4
      default:
        return 1
    }
  }

  const getStepDescription = () => {
    switch (followUpPhase) {
      case 'select-problem':
        return 'Select the problem to add a follow-up to'
      case 'enter-prompt':
        return 'Type the follow-up prompt and press Enter'
      case 'enter-time-complexity':
        return 'Enter time complexity (e.g., O(n)) or press Enter to skip'
      case 'enter-space-complexity':
        return 'Enter space complexity (e.g., O(1)) or press ⌘+Enter to save'
      default:
        return ''
    }
  }

  if (followUpPhase === 'select-problem') {
    return (
      <div style={mergeHeaderStyle}>
        <div style={{ fontWeight: 600, marginBottom: '4px', color: '#7c3aed' }}>
          Add Follow-up
        </div>
        <div style={hintTextStyle}>
          Step {getStepNumber()}: {getStepDescription()}
        </div>
      </div>
    )
  }

  return (
    <div style={mergeHeaderStyle}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#7c3aed' }}>
        Add Follow-up
      </div>

      {/* Show selected problem */}
      {targetProblem && (
        <div style={seriesProblemStyle}>
          <span style={{ ...seriesNumberStyle, backgroundColor: '#7c3aed' }}>
            ✓
          </span>
          <SourceIcon
            sourceIcon={targetProblem.sourceIcon}
            source={targetProblem.source}
          />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>
            {targetProblem.externalId ? `#${targetProblem.externalId} - ` : ''}
            {targetProblem.name}
          </span>
        </div>
      )}

      {/* Show collected data */}
      <div style={{ marginTop: '8px', marginBottom: '8px' }}>
        {/* Show prompt if entered */}
        {followUpInput.prompt && (
          <div style={completedItemStyle}>
            <span style={completedLabelStyle}>Prompt:</span>
            <span style={completedValueStyle}>
              {followUpInput.prompt.length > 60
                ? `${followUpInput.prompt.substring(0, 60)}...`
                : followUpInput.prompt}
            </span>
          </div>
        )}

        {/* Show time complexity if entered */}
        {followUpInput.expectedTimeComplexity && (
          <div style={completedItemStyle}>
            <span style={completedLabelStyle}>Time:</span>
            <span style={completedValueStyle}>
              {followUpInput.expectedTimeComplexity}
            </span>
          </div>
        )}

        {/* Show space complexity placeholder if in that phase */}
        {followUpPhase === 'enter-space-complexity' &&
          !followUpInput.expectedTimeComplexity && (
            <div
              style={{
                ...completedItemStyle,
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
              }}
            >
              <span style={{ ...completedLabelStyle, color: '#92400e' }}>
                Time:
              </span>
              <span style={{ fontSize: '12px', color: '#a16207' }}>
                (skipped)
              </span>
            </div>
          )}
      </div>

      {/* Show existing follow-ups count if in prompt phase */}
      {followUpPhase === 'enter-prompt' && existingFollowUps.length > 0 && (
        <div
          style={{
            fontSize: '11px',
            color: '#6b7280',
            marginBottom: '8px',
            padding: '4px 8px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
          }}
        >
          {existingFollowUps.length} existing follow-up
          {existingFollowUps.length > 1 ? 's' : ''}
        </div>
      )}

      <div style={hintTextStyle}>
        Step {getStepNumber()}: {getStepDescription()}
      </div>
    </div>
  )
}
